import React, { useState, createContext, useEffect, useCallback, useMemo, useRef } from 'react'
import { ReadObject, SaveObject } from '../services/storage'
import { ReadMe } from 'services/me'
import { isProfileNative } from 'services/profile'
import { captureSessionState, isSessionStateCurrent } from 'services/session'
import { languages } from 'utils/options'
import { apiRequest } from 'utils'
import { defaultThemeMode } from 'ui/theme-color'

export const CoreContext = createContext({})

const getSystemThemeMode = () => {
	if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
		return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
	}

	return defaultThemeMode
}

const getInitialThemePreference = () => {
	const storedTheme = ReadObject('theme_mode')
	if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system') {
		return storedTheme
	}

	return 'system'
}

export const CoreState = ({ children }) => {

	const [modal, setModal] = useState(null)
	const [reload, setReload] = useState(false)
	const [searchExpression, setSearchExpression] = useState("")
	const [user, setUser] = useState(() => isProfileNative(ReadObject('user')) ? null : ReadObject('user') || [])
	const [lists, setLists] = useState(ReadObject('lists') ? ReadObject('lists') : {})
	const [notifications, setNotifications] = useState([])
	const [notificationsLoading, setNotificationsLoading] = useState(false)
	const [notificationsLoaded, setNotificationsLoaded] = useState(false)
	const [notificationsError, setNotificationsError] = useState(false)
	const notificationsUserIdRef = useRef(null)

	const [filtered, setFiltered] = useState(ReadObject('filtered') ? ReadObject('filtered') : null);
	const [themePreference, setThemePreference] = useState(getInitialThemePreference);
	const [systemThemeMode, setSystemThemeMode] = useState(getSystemThemeMode);
	const themeMode = useMemo(() => themePreference === 'system' ? systemThemeMode : themePreference, [systemThemeMode, themePreference])

	const [language, setLanguage] = useState(ReadObject('language') ? ReadObject('language') : languages?.[0]?.id)
	const [translation, setTranslation] = useState(ReadObject('translation') ? ReadObject('translation') : null)

	const reloadMe = useCallback(async ({ background = false } = {}) => {
		const snapshot = captureSessionState()
		const startedPath = window.location.pathname
		const result = await apiRequest(() => ReadMe())
		if (!isSessionStateCurrent(snapshot) || window.location.pathname !== startedPath) return false
		if (result?.id) {
			setUser(current => JSON.stringify(current) === JSON.stringify(result) ? current : result)
		} else if (!background || [401, 403].includes(result?.error?.status)) {
			setUser(current => isProfileNative(current) ? null : current)
		}
		return result
	}, [])

	const applyThemeMode = useCallback((nextTheme) => {
		if (typeof nextTheme === 'function') {
			setThemePreference((currentPreference) => {
				const currentMode = currentPreference === 'system' ? systemThemeMode : currentPreference
				const resolvedTheme = nextTheme(currentMode)
				return resolvedTheme === 'light' || resolvedTheme === 'dark' || resolvedTheme === 'system'
					? resolvedTheme
					: currentPreference
			})
			return
		}

		if (nextTheme === 'light' || nextTheme === 'dark' || nextTheme === 'system') {
			setThemePreference(nextTheme)
		}
	}, [systemThemeMode])

	const toggleThemeMode = useCallback(() => {
		setThemePreference((currentPreference) => {
			const currentMode = currentPreference === 'system' ? systemThemeMode : currentPreference
			return currentMode === 'dark' ? 'light' : 'dark'
		})
	}, [systemThemeMode])

	const contextValue = useMemo(() => ({
		user, setUser,
		modal, setModal,
		reloadMe,
		themeMode, themePreference, setThemeMode: applyThemeMode, setThemePreference, toggleThemeMode,
		language, setLanguage,
		translation, setTranslation,
		searchExpression, setSearchExpression,
		notifications, setNotifications,
		notificationsLoading, setNotificationsLoading,
		notificationsLoaded, setNotificationsLoaded,
		notificationsError, setNotificationsError,
		notificationsUserIdRef,
		lists, setLists,
		reload, setReload,
		filtered, setFiltered
	}), [user, modal, reloadMe, themeMode, themePreference, applyThemeMode, toggleThemeMode, language, translation, searchExpression, notifications, notificationsLoading, notificationsLoaded, notificationsError, lists, reload, filtered])

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return undefined
		}

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
		const handleChange = (event) => {
			setSystemThemeMode(event.matches ? 'dark' : 'light')
		}

		setSystemThemeMode(mediaQuery.matches ? 'dark' : 'light')

		if (typeof mediaQuery.addEventListener === 'function') {
			mediaQuery.addEventListener('change', handleChange)
			return () => mediaQuery.removeEventListener('change', handleChange)
		}

		mediaQuery.addListener(handleChange)
		return () => mediaQuery.removeListener(handleChange)
	}, [])

	// to persist state when app reload  
	useEffect(() => { SaveObject('user', user); }, [user])
	useEffect(() => { SaveObject('language', language); }, [language])
	useEffect(() => { SaveObject('translation', translation); }, [translation])
	useEffect(() => { SaveObject('lists', lists); }, [lists])
	useEffect(() => { SaveObject('filtered', filtered); }, [filtered])
	useEffect(() => { SaveObject('theme_mode', themePreference); }, [themePreference])
	useEffect(() => {
		if (typeof document === 'undefined') return
		document.documentElement.style.colorScheme = themeMode
	}, [themeMode])

	return <CoreContext.Provider value={contextValue}>{children}</CoreContext.Provider>
}
